import React from 'react';
import { Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../redux/hooks';
import { RootState } from '../../../redux/rootReducer';
import CustomDropdown from '../../CustomDropdown/CustomDropdown';
import StyledButton from '../../Common/StyledButton';
import StyledSwitch from './StyledSwitch';
import { AIModelOptions } from '../../../types/coach';
import validationSchema from '../../../utils/validations/coachAISettings';
import { updateCoachSettings } from '../../../redux/coach/coachSlice';
import { StyledFormControlLabel } from '../../ClientDetail/PrivacySettings';
import createPermissionCheckers from '../../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../../types/commonTypes';

export default function AppSettings() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { coachAISettings, updateCoachAISettingsLoading } = useAppSelector(
    (state: RootState) => state.coach
  );

  const formik = useFormik({
    initialValues: coachAISettings,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (v) => {
      const payload = {
        id: coachAISettings.id,
        isNoteTakingAIEnabled: v.isNoteTakingAIEnabled,
        noteSummaryAIModel: v.noteSummaryAIModel,
        isMagicSuggestionEnabled: v.isMagicSuggestionEnabled,
        magicSuggestionAIModel: v.magicSuggestionAIModel,
      };
      dispatch(updateCoachSettings(payload));
    },
  });

  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;

  return (
    <Grid container gap="36px">
      <Typography variant="bodyBold" color="primary.main">
        {t('settings.appSettings.aiSettings.title')}
      </Typography>

      <form onSubmit={formik.handleSubmit} noValidate>
        <Grid container spacing={4.5}>
          <Grid item xs={12}>
            <StyledFormControlLabel
              control={
                <StyledSwitch
                  name="isNoteTakingAIEnabled"
                  checked={formik.values.isNoteTakingAIEnabled}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              }
              disabled={!checkPermissions(UserPermissionType.CORE_MODULE_UPDATE_APP_SETTINGS)}
              label={t('appSettings.assistedNoteTaking')}
              labelPlacement="start"
            />
            <CustomDropdown
              name="noteSummaryAIModel"
              options={AIModelOptions}
              label={t('appSettings.assistedNoteTakingAI')}
              value={formik.values.noteSummaryAIModel ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.noteSummaryAIModel ? formik.errors.noteSummaryAIModel : undefined
              }
              disabled={
                !formik.values.isNoteTakingAIEnabled ||
                !checkPermissions(UserPermissionType.CORE_MODULE_UPDATE_APP_SETTINGS)
              }
              isTranslationProvided
            />
          </Grid>
          <Grid item xs={12}>
            <StyledFormControlLabel
              control={
                <StyledSwitch
                  name="isMagicSuggestionEnabled"
                  checked={formik.values.isMagicSuggestionEnabled}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              }
              disabled={!checkPermissions(UserPermissionType.CORE_MODULE_UPDATE_APP_SETTINGS)}
              label={t('appSettings.magicSuggestions')}
              labelPlacement="start"
            />
            <CustomDropdown
              name="magicSuggestionAIModel"
              options={AIModelOptions}
              label={t('appSettings.magicSuggestionsAI')}
              value={formik.values.magicSuggestionAIModel ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.magicSuggestionAIModel
                  ? formik.errors.magicSuggestionAIModel
                  : undefined
              }
              isTranslationProvided
              disabled={
                !formik.values.isMagicSuggestionEnabled ||
                !checkPermissions(UserPermissionType.CORE_MODULE_UPDATE_APP_SETTINGS)
              }
            />
          </Grid>
          {checkPermissions(UserPermissionType.CORE_MODULE_UPDATE_APP_SETTINGS) && (
            <Grid item xs={12} display="flex" justifyContent="end">
              <StyledButton loading={updateCoachAISettingsLoading} loadingText={t('common.saving')}>
                {t('common.save')}
              </StyledButton>
            </Grid>
          )}
        </Grid>
      </form>
    </Grid>
  );
}
